/**
 * @fileoverview The gardener's live beds (status 1), one read under the shared
 * 'locations' key (LOCATIONS_QUERY_KEY), so a bed added, renamed or archived
 * anywhere is in every list that offers beds: the beds page (LocationsContext),
 * the plant form's bed list, the packet review and the welcome place step. It
 * needs no provider, so it works where LocationsContext is not mounted (the
 * plants route).
 *
 * Each bed comes with what the Beds card draws (W24 Job H): its growing plants
 * and plant_count, the removed ones (archived_plants), lately (its Lately) and sharing (its Share count). A bed
 * drop-down needs only names: the short list (useBedNames) is for that, and
 * the drop-downs move to it with W24 Job B.
 *
 * Where: LocationsContext, PlantFormDialog, PacketReview, WelcomePlace;
 * features/locations useBedSearch keeps its answers under this key too.
 * API: GET /locations?status=1 (getLocations).
 * Settings: none; the query key is this hook's own (the same word every invalidation of the beds uses), with the app's
 * language after it: a bed's kind and sun line and each plant's name come in the language asked for (B72), so a
 * change of language reads them again.
 *
 * @module useBeds
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getLocations, type Location } from '@/shared/api/locations';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** The query key the live beds are kept under ('locations'); a writer that changes a bed invalidates it. */
export const BEDS_KEY = 'locations';

/**
 * The live beds, in the order the server sends them.
 *
 * @param enabled - Whether to read them now (a closed dialog waits); true by default.
 * @returns The query: `data` is the beds (undefined until they arrive).
 */
export function useBeds(enabled = true): UseQueryResult<Location[], Error> {
  const { i18n: { language } } = useTranslation();
  return useQuery<Location[], Error>({
    queryKey: [BEDS_KEY, language],
    // The live ones. Retired locations have their own page and their own query.
    queryFn: () => getLocations(1),
    enabled,
  });
}
